<script setup>
import { ref, computed, onMounted } from 'vue';
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import store from '../../state/store';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from 'moment'
import { cloneDeep } from 'lodash';
import Swal from 'sweetalert2';

const title = ref('Batch Received by Couriers');

const items = ref([
  {
    text: "Home",
  },
  {
    text: "Tracking",
  },
  {
    text: "Batch Received by Couriers",
    active: true,
  },
]);

const submitted = ref(false);

const inputBatchNumber = ref(null);

const notification = computed(() => store.state.notification)
const loading = computed(() => store.state.globals.loading);

const search = ref({
  batch_number: '',
  date: moment().format('YYYY/MM/DD')
})

const cloneSearch = ref(null);

const humanfriendlyConfig = ref({
  altInput: true,
  altFormat: "F j, Y",
  dateFormat: "Y/m/d"
});

const results = computed(() => store.state.tracking.batchNumberNovs);

const searchBatchNumber = async () => {
  cloneSearch.value = null;

  try {
    submitted.value = true;
    await store.dispatch('tracking/getAllBatchNumberNovs', search.value)
    
    cloneSearch.value = cloneDeep(search.value);
  } catch (err) {
    console.log(err);
  }
}

const updateTrackingEntries = async () => {
  if (cloneSearch.value === null) return;

  try {
    Swal.fire({
            title: "Do you want to continue?",
            text: "Each of the tracking number in the batch will be marked as received?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#34c38f",
            cancelButtonColor: "#f46a6a",
            confirmButtonText: "Yes",
        }).then(async (res) => {
            if (res.value) {
                await store.dispatch('tracking/updateTrackingEntries', cloneSearch.value.batch_number);
                Swal.fire(
                    "Update Tracking Entries!", 
                    `Successfully updated the tracking number entries to batch number: ${cloneSearch.value.batch_number}`, 
                    "success"
                );

                search.value.batch_number = '';
                search.value.date = moment().format('YYYY/MM/DD');
                cloneSearch.value = null;
            }
        });
  } catch (err) {
    console.log(err);
  }
}

onMounted(() => {
  inputBatchNumber.value.focus();
})
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-md-12 col-lg-4">
        <div class="card">
          <div class="card-header">
            <h4>Batch Received by Couriers</h4>
            <div class="text-muted">Search and update tracking entries</div>
          </div>
          <div class="card-body">
              <form @submit.prevent="searchBatchNumber">
                <div class="mb-3">
                  <label for="batch_number" class="form-label">Batch No.</label>
                  <input 
                    :class="{
                      'is-invalid': submitted && !search.batch_number
                    }"
                    type="text" autofocus v-model="search.batch_number" ref="inputBatchNumber" class="form-control" id="batch_number" placeholder="Search batch number...">
                  <div
                    v-if="submitted && !search.batch_number"
                    class="invalid-feedback"
                  >
                    <span>Batch Number is required</span>
                  </div>
                </div>
                <div class="mb-3">
                  <label for="batch_date" class="form-label">Date</label>
                   <flatPickr
                    id="date"
                    v-model="search.date"
                    :config="humanfriendlyConfig"
                    class="form-control flatpickr-input"
                ></flatPickr>
                </div>
                <div class="mb-3">
                  <button :disabled="!search.batch_number" class="btn btn-primary">
                    <i class="bx bx-search mr-2 align-middle fs-4 text"></i> Search
                  </button>
                </div>
              </form>
          </div>
        </div>
      </div>
      <div class="col-md-12 col-lg-8">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-body">
              <div v-if="results.length && submitted" class="table-card m-1">
                <div class="d-flex justify-content-between mb-3">
                  <h4>Batch Number: {{ cloneSearch ? cloneSearch.batch_number : 'N/A' }}</h4>
                  <button v-if="results.length && submitted" @click.prevent="updateTrackingEntries" class="btn btn-info">
                    <i class=" bx bx-save mr-2 align-middle fs-4 text"></i> Update Tracking Entries
                  </button>
                </div>
                <div class="text-muted mb-3">
                  By pressing the "Update" button. Each of the tracking number in the batch will be marked as received by QPAX and In transit to Violator.
                </div>
                <table class="table table-nowrap">
                    <thead class="table-light">
                        <tr>
                            <th scope="col">Tracking No.</th>
                            <th scope="col">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="result in results" :key="result.id">
                            <td>
                              {{ result.tracking_number ? result.tracking_number : 'No tracking number' }}
                            </td>
                            <td>
                              {{ result.tracking_status_type ? result.tracking_status_type.system_name : 'No status' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
              </div>
              <div v-else-if="results.length === 0 && !notification.message && submitted">
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                      <h4 class="mt-4 fw-semibold">Tracking Entries</h4>
                      <p class="text-danger mt-3 fs-16 text"> 
                        Batch does not have tracking number yet. Please add tracking number in <strong><router-link to="/delivery/add-tracking">Delivery > Add Tracking tab</router-link></strong>
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/file.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
               <div v-else-if="notification && notification.message  && submitted">
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                      <h4 class="mt-4 fw-semibold">Tracking Entries</h4>
                      <p class="text-danger mt-3 fs-16 text"> 
                        {{ notification.message }}
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/error500.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                      <h4 class="mt-4 fw-semibold">Tracking Entries</h4>
                      <p class="text-muted mt-3 fs-16 text"> 
                        Search batch number and batch date
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/file.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </Layout>
</template>